import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Mic, MessageSquare, DollarSign, FileEdit, BookOpen, Hammer, NotebookPen, ArrowRight, MessagesSquare } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { MarkdownEditor } from '@/components/MarkdownEditor';
import { Button } from '@/components/ui/button';
import { relativeTime, shortDate } from '@/lib/format';
import type { ProjectActivityDto, ProjectActivitySource } from '@cc/types';

const SOURCE_ICONS: Record<ProjectActivitySource, typeof Mail> = {
  EMAIL: Mail,
  VOICE_MEMO: Mic,
  TELEGRAM_TEXT: MessageSquare,
  EXPENSE: DollarSign,
  DRAFT_SENT: FileEdit,
  BRIEF_NOTE: BookOpen,
  RENO_CHAT: Hammer,
  MANUAL: NotebookPen,
};

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: qk.project(id ?? ''),
    queryFn: () => api.getProject(id!),
    enabled: Boolean(id),
  });

  const saveNotes = useMutation({
    mutationFn: (notesMd: string) => api.updateProject(id!, { notesMd }),
    onSuccess: (res) => {
      queryClient.setQueryData(qk.project(id!), (old: typeof data) =>
        old ? { ...old, project: res.project } : old,
      );
      void queryClient.invalidateQueries({ queryKey: qk.projects });
      toast.success('Notes saved.');
    },
    onError: () => {
      toast.error('Failed to save notes. Try again.');
    },
  });

  if (!id) return <p>Bad route.</p>;
  if (error) return <p className="text-sm text-destructive">Failed to load project.</p>;
  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  const { project, activities } = data;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/projects"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Projects
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{project.name}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {project.type.toLowerCase()} · last activity {relativeTime(project.lastActivityAt)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/projects/${project.id}/chat`}>
              <Button variant="outline" size="sm">
                <MessagesSquare className="h-3.5 w-3.5" /> Project chat
                <ArrowRight className="h-3 w-3" />
              </Button>
            </Link>
            {project.type === 'PROPERTY' && (
              <Link to={`/projects/${project.id}/renovation`}>
                <Button variant="outline" size="sm">
                  <Hammer className="h-3.5 w-3.5" /> Renovation hub
                  <ArrowRight className="h-3 w-3" />
                </Button>
              </Link>
            )}
            <Badge variant={project.status === 'ACTIVE' ? 'success' : project.status === 'DORMANT' ? 'warning' : 'muted'}>
              {project.status.toLowerCase()}
            </Badge>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 120, damping: 14 }}
        className="grid grid-cols-1 gap-6 lg:grid-cols-3"
      >
        <section className="lg:col-span-2 space-y-4">
          <Card>
            <CardContent className="p-5">
              <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Notes
              </h2>
              <MarkdownEditor
                value={project.notesMd}
                onSave={async (next) => {
                  await saveNotes.mutateAsync(next);
                }}
                placeholder="Project context, hidden constraints, decisions in flight. The disambiguator and project query both read these."
              />
            </CardContent>
          </Card>
        </section>

        <aside className="space-y-4">
          <Card>
            <CardContent className="p-5">
              <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                At a glance
              </h2>
              <dl className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Activities</dt>
                  <dd>{project.activityCount}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Last activity</dt>
                  <dd>{relativeTime(project.lastActivityAt)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Created</dt>
                  <dd>{shortDate(project.createdAt)}</dd>
                </div>
              </dl>
              {project.aliases.length > 0 && (
                <div className="mt-4">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Aliases
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {project.aliases.map((a) => (
                      <Badge key={a} variant="outline">
                        {a}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {project.keyEntities.length > 0 && (
                <div className="mt-3">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Key entities
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {project.keyEntities.map((e) => (
                      <Badge key={e} variant="outline">
                        {e}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Activity feed
              </h2>
              <ActivityFeed activities={activities} />
            </CardContent>
          </Card>
        </aside>
      </motion.div>
    </div>
  );
}

function ActivityFeed({ activities }: { activities: ProjectActivityDto[] }) {
  if (activities.length === 0) {
    return (
      <p className="text-sm italic text-muted-foreground">
        No activity yet. Emails, voice memos, drafts, and renovation chat will land here as later
        phases ship.
      </p>
    );
  }
  return (
    <ul className="space-y-3">
      {activities.map((a) => {
        const Icon = SOURCE_ICONS[a.source];
        return (
          <li key={a.id} className="flex items-start gap-3 text-sm">
            <div className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-md bg-muted">
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate">{a.summary}</p>
              <p className="text-[11px] text-muted-foreground">
                {a.source.toLowerCase().replace('_', ' ')} · {relativeTime(a.occurredAt)}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
